/**
 * Font Configuration for WoodAlps Website
 * 
 * This module configures and exports all font families used throughout the WoodAlps website.
 * It handles both Google Fonts integration via Next.js font optimization and custom fonts.
 * 
 * Font Strategy:
 * - Roboto: Primary font for body text and general content
 * - Montserrat: Aliased to Roboto for compatibility with existing designs
 * - Neuropol: Custom brand font for headings and special text (loaded via CSS)
 * 
 * Performance Features:
 * - Font display swap for better loading performance
 * - Optimized subsets for faster loading
 * - Multiple font weights available
 * - CSS custom properties for easy theme integration
 * 
 * @description Font configuration with Google Fonts optimization and custom font support
 */

import { Roboto } from 'next/font/google'

/**
 * Primary Roboto font configuration
 * Used for body text, navigation, and general interface elements
 * 
 * Features:
 * - Latin subset for optimal loading
 * - Multiple weights: light (300), regular (400), medium (500), bold (700)
 * - CSS variable: --font-roboto
 * - Font display swap for performance
 */
export const roboto = Roboto({
  subsets: ['latin'],
  weight: ['300', '400', '500', '700'],
  variable: '--font-roboto',
  display: 'swap',
})

/**
 * Montserrat font alias
 * Currently aliased to Roboto for consistency and performance
 * This allows for easy font switching in the future if needed
 * 
 * @deprecated Consider using roboto directly for new components
 */
export const montserrat = roboto

/**
 * Neuropol custom font configuration
 * Brand-specific font used for headings and special typography
 * 
 * Note: This font is loaded via CSS @font-face in globals.css
 * rather than Next.js font system due to custom font file requirements
 * 
 * Features:
 * - CSS variable: --font-neuropol
 * - Fallback to system sans-serif
 * - Used primarily for headings and brand elements
 */
export const neuropol = { 
  variable: '--font-neuropol',
  className: 'font-sans' // Fallback to sans-serif if custom font fails
} 